import { useEffect, useId, useRef, useState } from "react";
import type {
  IaChatEntity,
  StoreComparisonQuery,
  StoreComparisonTable,
} from "@bitacorapro/api-types";
import { CompareStoresTable } from "../components/CompareStoresTable";
import { Icon } from "../components/icons";
import { MarkdownMessage } from "../components/MarkdownMessage";
import { ApiError } from "../lib/api/error";
import { postIaChat, type IaChatTurn } from "../lib/api/endpoints/ia";
import { useCompareStores } from "../lib/api/queries";

type Msg =
  | { role: "user"; id: string; content: string }
  | { role: "ai"; id: string; kind: "loading" }
  | {
      role: "ai";
      id: string;
      kind: "answer";
      content: string;
      grounded: boolean;
      entities?: IaChatEntity[];
      // ADR-0167 — a table the model asked for; every cell from core-api.
      table?: StoreComparisonTable;
    }
  | { role: "ai"; id: string; kind: "error"; message: string; question: string }
  // «Comparar tiendas» chip: a deterministic table, no model call. Not a
  // chat turn, so it never goes back as history.
  | { role: "ai"; id: string; kind: "compare"; query: StoreComparisonQuery };

// The chip's query: every column, the whole scope.
const COMPARE_ALL: StoreComparisonQuery = {};

// Entity handles only mean something inside the turn that minted them, so
// a past answer goes back as history with each `[[T3]]` spelled out as
// its label — the model reads the name, never a stale handle.
const ENTITY_TOKEN_RE = /\[\[([TRO]\d{1,4})\]\]/g;
function historyText(content: string, entities: readonly IaChatEntity[] | undefined): string {
  const labels = new Map((entities ?? []).map((e) => [e.handle, e.label]));
  return content.replace(ENTITY_TOKEN_RE, (_m, h: string) => labels.get(h) ?? "");
}

// Suggested questions — chips that pre-fill and submit immediately.
// Every chip must be answerable from the facts block core-api sends
// (apps/core-api/src/routes/ia-chat.ts): all-time aggregates, each
// store's state from its last closed report (zona, marca, Score and its
// trend, % per checklist section, ADR-0137), a sample of conforming
// items, the recent open-observations list, and each store's periodic
// maintenance items with their due state (ADR-0083). No date cuts and no
// portfolio coverage — so no «este mes» or «cobertura» chips until that
// data ships.
const SUGGESTED = [
  "¿Qué tiendas tienen más observaciones críticas abiertas?",
  "¿Qué tiendas tienen mejor cumplimiento por sección?",
  "¿Qué tiendas bajaron su score en el último reporte?",
  "¿Qué tiendas tienen mantenciones vencidas?",
  "¿Qué proveedor tiene más atrasos?",
  "¿Hay patrones recurrentes por zona?",
  "¿En qué categorías se concentran las observaciones?",
];

/** «1 tienda» / «3 tiendas» — for the live region. */
function storesCount(n: number): string {
  return n === 1 ? "1 tienda" : `${n} tiendas`;
}

let seq = 0;
const nextId = (): string => `m${++seq}`;

export function IAInsights() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // Polite live-region string (WCAG 2.2 SC 4.1.3) — screen readers learn
  // the answer arrived without focus moving. Kept separate from the
  // visible thread so only a concise summary is announced.
  const [announce, setAnnounce] = useState("");
  // Once the conversation starts, the question chips fold behind a
  // «Sugerencias» toggle: at 1280×800 seven wrapping chips took four rows
  // and left the thread ~250px, too little for a comparison table.
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsId = useId();
  const compact = messages.length > 0;
  const abortRef = useRef<AbortController | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);

  // The thread is viewport-bounded and scrolls internally, so a new turn
  // (question, pending bubble, answer) would land out of view unless we
  // follow it. Instant scroll: no motion to honor prefers-reduced-motion.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  function compare() {
    setShowSuggestions(false);
    setMessages((prev) => [
      ...prev,
      { role: "ai", id: nextId(), kind: "compare", query: COMPARE_ALL },
    ]);
    setAnnounce("Preparando la comparación de tiendas…");
  }

  async function send(q: string) {
    const text = q.trim();
    if (!text || busy) return;

    // Cancel any in-flight request from a previous send.
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    const aiId = nextId();
    // Build the conversation history (user + assistant turns) from the
    // current thread before we append the new turn.
    const history: IaChatTurn[] = messages.flatMap((m): IaChatTurn[] => {
      if (m.role === "user") return [{ role: "user", content: m.content }];
      if (m.role === "ai" && m.kind === "answer") {
        // An empty turn would fail the request schema (content min 1) and
        // break every later question; it carries nothing for the model.
        const content = historyText(m.content, m.entities).trim();
        return content ? [{ role: "assistant", content }] : [];
      }
      return [];
    });

    setDraft("");
    setShowSuggestions(false);
    setBusy(true);
    setAnnounce("Enviando pregunta…");
    setMessages((prev) => [
      ...prev,
      { role: "user", id: nextId(), content: text },
      { role: "ai", id: aiId, kind: "loading" },
    ]);

    try {
      const { answer, grounded, entities, table } = await postIaChat(
        { question: text, history },
        ac.signal
      );
      setMessages((prev) =>
        prev.map((m) =>
          m.role === "ai" && m.id === aiId
            ? {
                role: "ai",
                id: aiId,
                kind: "answer",
                content: answer,
                grounded,
                ...(entities ? { entities } : {}),
                ...(table ? { table } : {}),
              }
            : m
        )
      );
      setAnnounce(
        table
          ? `Respuesta recibida. Tabla de comparación lista, ${storesCount(table.rows.length)}.`
          : grounded
            ? "Respuesta recibida."
            : "Respuesta recibida. No está verificada con tus datos."
      );
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const message =
        err instanceof ApiError
          ? err.message
          : "No pudimos obtener una respuesta. Revisa tu conexión e intenta de nuevo.";
      setMessages((prev) =>
        prev.map((m) =>
          m.role === "ai" && m.id === aiId
            ? { role: "ai", id: aiId, kind: "error", message, question: text }
            : m
        )
      );
      // Restore the question so the user can resend without retyping.
      setDraft(text);
      setAnnounce("No se pudo obtener respuesta. Intenta de nuevo.");
    } finally {
      if (!ac.signal.aborted) setBusy(false);
    }
  }

  return (
    <div className="page ia-page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">IA · Insights de tus tiendas</div>
          <h1 className="page-header__title">AI Insights</h1>
          <p className="page-header__sub">
            Pregúntale a la IA sobre tus tiendas: observaciones abiertas, proveedores con atrasos,
            patrones por zona o categoría. Responde con los datos de tus reportes; si no encuentra
            información suficiente, te lo dice.
          </p>
        </div>
      </div>

      <div className="ia-layout">
        <div className="ia-main">
          <div
            ref={threadRef}
            className="ia-thread"
            role="log"
            aria-label="Conversación con la IA"
            aria-busy={busy}
            // Scrollable region must be keyboard-reachable (WCAG 2.1.1);
            // Firefox/Safari do not focus scroll containers on their own.
            tabIndex={0}
          >
            {messages.length === 0 && (
              <div className="ia-msg ia-msg--ai">
                <div className="ia-msg__bubble">
                  <p>
                    Hazme una pregunta sobre tus tiendas. Puedo cruzar observaciones, severidades,
                    SLAs de proveedores, el score y el cumplimiento por sección del último reporte
                    de cada tienda, y el estado de sus mantenciones.
                  </p>
                </div>
              </div>
            )}
            {messages.map((m) => (
              <IaMessage key={m.id} m={m} onRetry={(q) => void send(q)} onAnnounce={setAnnounce} />
            ))}
          </div>

          {/* Visually-hidden polite live region for screen readers. */}
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {announce}
          </div>

          <div
            className={`ia-suggested${compact ? " ia-suggested--compact" : ""}`}
            role="group"
            aria-label="Preguntas sugeridas"
          >
            {/* An action, not a question: it builds the table from the
                database without asking the model. */}
            <button
              type="button"
              className="ia-sug-btn ia-sug-btn--action"
              disabled={busy}
              onClick={compare}
            >
              <Icon.Table s={12} /> Comparar tiendas
            </button>
            {compact && (
              <button
                type="button"
                className="ia-sug-btn ia-sug-btn--toggle"
                aria-expanded={showSuggestions}
                aria-controls={suggestionsId}
                onClick={() => setShowSuggestions((v) => !v)}
              >
                <Icon.Sparkles s={12} /> Sugerencias
                <span className="ia-sug-btn__chev" aria-hidden="true">
                  <Icon.ChevronDown s={12} />
                </span>
              </button>
            )}
            {/* Disclosure panel: hidden (not unmounted) while folded, so
                aria-controls always points at an element. */}
            <div
              id={suggestionsId}
              className="ia-suggested__list"
              hidden={compact && !showSuggestions}
            >
              {SUGGESTED.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="ia-sug-btn"
                  disabled={busy}
                  onClick={() => void send(q)}
                >
                  <Icon.Sparkles s={12} /> {q}
                </button>
              ))}
            </div>
          </div>

          <div className="ia-composer">
            <div className="ia-composer__box">
              <Icon.Sparkles s={16} />
              <textarea
                className="ia-composer__input"
                placeholder="Pregunta lo que necesites…"
                aria-label="Pregunta a la IA"
                aria-describedby="ia-composer-hint"
                value={draft}
                readOnly={busy}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void send(draft);
                  }
                }}
              />
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={busy || !draft.trim()}
                aria-busy={busy}
                onClick={() => void send(draft)}
              >
                {busy ? (
                  <>
                    <span className="ia-thinking-dot" />
                    <span className="ia-thinking-dot" />
                    <span className="ia-thinking-dot" />
                    Enviando…
                  </>
                ) : (
                  <>
                    <Icon.Send s={14} /> Enviar
                  </>
                )}
              </button>
            </div>
            <div className="ia-composer__hint" id="ia-composer-hint">
              ⌘ Enter para enviar
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function IaMessage({
  m,
  onRetry,
  onAnnounce,
}: {
  m: Msg;
  onRetry: (q: string) => void;
  onAnnounce?: (text: string) => void;
}) {
  if (m.role === "user") {
    return (
      <div className="ia-msg ia-msg--user">
        <div className="ia-msg__bubble">
          <span className="sr-only">Tú: </span>
          {m.content}
        </div>
      </div>
    );
  }
  if (m.kind === "loading") {
    return (
      <div className="ia-msg ia-msg--ai">
        <div className="ia-msg__bubble ia-msg__bubble--loading" aria-label="La IA está pensando">
          <span className="ia-thinking-dot" />
          <span className="ia-thinking-dot" />
          <span className="ia-thinking-dot" />
        </div>
      </div>
    );
  }
  if (m.kind === "compare") {
    return <CompareMessage query={m.query} onAnnounce={onAnnounce} />;
  }
  if (m.kind === "error") {
    return (
      <div className="ia-msg ia-msg--ai">
        <div className="ia-msg__bubble ia-msg__bubble--error" role="alert">
          <span className="sr-only">Error: </span>
          <p>{m.message}</p>
          <button type="button" className="ia-retry-btn" onClick={() => onRetry(m.question)}>
            Reintentar esta pregunta →
          </button>
        </div>
      </div>
    );
  }
  // answer
  return (
    <div className="ia-msg ia-msg--ai">
      <div
        className={`ia-msg__bubble${m.grounded ? "" : " ia-msg__bubble--ungrounded"}${
          m.table ? " ia-msg__bubble--table" : ""
        }`}
      >
        <span className="sr-only">IA: </span>
        {!m.grounded && (
          <span className="ia-msg__ungrounded-badge">
            <Icon.Warning s={12} /> No verificado con tus datos
          </span>
        )}
        <MarkdownMessage source={m.content} {...(m.entities ? { entities: m.entities } : {})} />
        {m.table && <CompareStoresTable table={m.table} query={m.table.query} />}
      </div>
    </div>
  );
}

function CompareMessage({
  query,
  onAnnounce,
}: {
  query: StoreComparisonQuery;
  onAnnounce: ((text: string) => void) | undefined;
}) {
  const { data, isPending, isError, refetch } = useCompareStores(query);
  const rows = data?.rows.length;
  useEffect(() => {
    if (rows !== undefined) onAnnounce?.(`Tabla de comparación lista, ${storesCount(rows)}.`);
  }, [rows, onAnnounce]);

  if (isPending) {
    return (
      <div className="ia-msg ia-msg--ai">
        <div
          className="ia-msg__bubble ia-msg__bubble--loading"
          aria-label="Preparando la comparación"
        >
          <span className="ia-thinking-dot" />
          <span className="ia-thinking-dot" />
          <span className="ia-thinking-dot" />
        </div>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="ia-msg ia-msg--ai">
        <div className="ia-msg__bubble ia-msg__bubble--error" role="alert">
          <span className="sr-only">Error: </span>
          <p>No pudimos preparar la comparación. Revisa tu conexión e intenta de nuevo.</p>
          <button type="button" className="ia-retry-btn" onClick={() => void refetch()}>
            Reintentar →
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="ia-msg ia-msg--ai">
      <div className="ia-msg__bubble ia-msg__bubble--table">
        <span className="sr-only">IA: </span>
        <p className="ia-cmp__title">Comparación de tus tiendas con los datos de tu cuenta.</p>
        <CompareStoresTable table={data} query={query} />
      </div>
    </div>
  );
}
